import { useEffect } from 'react';
import { useAccount } from 'wagmi';
import { clearCachedSignatures, hasValidCachedSignatures } from '../utils/signatureCache';
import { appKit, wagmiAdapter } from '../config/appkit';

/**
 * WalletConnectionManager Component
 *
 * This component handles wallet connection persistence and cleanup.
 * It doesn't render anything visible but works in the background to:
 * 1. Store the connection state in localStorage
 * 2. Clean up cached signatures when the wallet is disconnected
 * 3. Handle wallet address changes
 * 4. Ensure proper separation between AppKit and Passport authentication
 */
export default function WalletConnectionManager() {
  const { address, isConnected } = useAccount();

  // Helper function to check if Passport is the active provider
  const isPassportActive = () => {
    const walletProvider = localStorage.getItem('wallet_provider');
    const passportAddress = localStorage.getItem('passport_address');
    const passportUserInfo = localStorage.getItem('passport_user_info');

    return walletProvider === 'passport' || (passportAddress && passportUserInfo);
  };

  // Store connection state and handle address changes
  useEffect(() => {
    // Skip if Passport is the active provider
    if (isPassportActive()) {
      console.log('Passport is the active provider, skipping AppKit connection management');
      return;
    }

    // Store connected state for AppKit
    if (isConnected && address) {
      localStorage.setItem('wallet_connected', 'true');
      localStorage.setItem('wallet_address', address);
      localStorage.setItem('wallet_last_connected', Date.now().toString());
      localStorage.setItem('wallet_provider', 'appkit'); // Explicitly mark as AppKit
      console.log('AppKit wallet connection established and cached');

      // Check if we have valid cached signatures for this address
      const hasSignatures = hasValidCachedSignatures(address);
      console.log(`AppKit wallet has valid cached signatures: ${hasSignatures}`);
    } else if (!isPassportActive()) {
      // Only clean up if we're not using Passport
      // If disconnected, clean up AppKit-related storage
      localStorage.removeItem('wallet_connected');

      // If there was a previously connected address, clear its signatures
      const previousAddress = localStorage.getItem('wallet_address');
      if (previousAddress && previousAddress !== address) {
        clearCachedSignatures(previousAddress);
        localStorage.removeItem('wallet_address');
        localStorage.removeItem('wallet_last_connected');
      }
    }
  }, [isConnected, address]);

  // Attempt to restore connection on mount
  useEffect(() => {
    const restoreConnection = async () => {
      try {
        // Skip completely if Passport is the active provider
        if (isPassportActive()) {
          console.log('Passport provider detected, skipping AppKit auto-reconnection');
          return;
        }

        // Check if we have stored credentials
        const storedAddress = localStorage.getItem('wallet_address');
        const lastConnected = localStorage.getItem('wallet_last_connected');
        const walletProvider = localStorage.getItem('wallet_provider');
        const isRecent = lastConnected &&
          (Date.now() - parseInt(lastConnected, 10) < 14 * 24 * 60 * 60 * 1000);

        // Double-check we're not using Passport
        if (walletProvider === 'passport') {
          console.log('Passport provider detected, skipping AppKit auto-reconnection');
          return;
        }

        if (storedAddress && isRecent && !isConnected) {
          console.log('Attempting to restore AppKit wallet connection...');

          // Try to reconnect using wagmiAdapter first
          try {
            await wagmiAdapter.connect();
            console.log('AppKit connection restored via wagmiAdapter');

            // Mark as AppKit provider
            localStorage.setItem('wallet_provider', 'appkit');
          } catch (error) {
            console.log('Failed to restore via wagmiAdapter, trying appKit...', error);
            try {
              await appKit.connect();
              console.log('AppKit connection restored via appKit');

              // Mark as AppKit provider
              localStorage.setItem('wallet_provider', 'appkit');
            } catch (innerError) {
              console.error('Failed to restore connection via appKit:', innerError);
            }
          }
        }
      } catch (error) {
        console.error('Error restoring wallet connection:', error);
      }
    };

    restoreConnection();
  }, [isConnected]);

  // Handle browser refresh by checking cached connection state
  useEffect(() => {
    const checkCachedConnection = () => {
      // Skip completely if Passport is the active provider
      if (isPassportActive()) {
        console.log('Passport provider detected, skipping AppKit connection check');
        return;
      }

      // This is handled by the auto-connect features in the providers,
      // but we log for visibility
      const cachedConnectionState = localStorage.getItem('wallet_connected');
      const lastConnected = localStorage.getItem('wallet_last_connected');
      const cachedAddress = localStorage.getItem('wallet_address');
      const walletProvider = localStorage.getItem('wallet_provider');

      // Double-check we're not using Passport
      if (walletProvider === 'passport') {
        console.log('Passport provider detected, skipping AppKit auto-reconnection');
        return;
      }

      if (cachedConnectionState === 'true' && cachedAddress) {
        // Check if the connection is recent (within 14 days)
        const isRecent = lastConnected &&
          (Date.now() - parseInt(lastConnected, 10) < 14 * 24 * 60 * 60 * 1000);

        // Check if we have valid cached signatures
        const hasSignatures = hasValidCachedSignatures(cachedAddress);

        console.log(`Found cached AppKit wallet connection: Address=${cachedAddress}, Recent=${isRecent}, Has Signatures=${hasSignatures}, Provider=${walletProvider || 'unknown'}`);

        if (isRecent || hasSignatures) {
          console.log('Valid cached AppKit wallet connection found, reconnecting automatically');
          // Update the last connected timestamp to extend the session
          localStorage.setItem('wallet_last_connected', Date.now().toString());

          // If we're not connected yet, try to reconnect
          if (!isConnected) {
            console.log('Attempting to reconnect AppKit wallet...');
            // This will trigger the reconnection effect
            localStorage.setItem('wallet_reconnect_requested', 'true');

            // Mark as AppKit provider if not already set
            if (!walletProvider) {
              localStorage.setItem('wallet_provider', 'appkit');
            }
          }
        }
      }
    };

    checkCachedConnection();

    // Listen for storage events from other tabs
    const handleStorageChange = (event) => {
      if (event.key === 'wallet_address' || event.key === 'wallet_connected' || event.key === 'wallet_provider') {
        checkCachedConnection();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [isConnected]);

  // This component doesn't render anything
  return null;
}
